import { Globe, Target, Trophy } from 'lucide-react';
import {
  getPublicCompetitionStatus,
  publicCompetitionTypeLabel,
  type CompetitionPhase,
  type EntryOpenness,
} from '../lib/publicCompetitionStatus';

type CompetitionCardCompetition = {
  id: string;
  name: string;
  competition_type?: string;
  competitionType?: string;
  start_date?: string | Date | null;
  startDate?: string | Date | null;
};

interface PublicCompetitionCardProps {
  competition: CompetitionCardCompetition;
  onClick: () => void;
  onMouseEnter?: () => void;
  totalEntrants?: number;
  remainingEntrants?: number | null;
  wcFirstKickoff?: Date | null;
  firstGameweekDeadline?: Date | null;
}

function StatusBadges({ status }: { status: { phase: CompetitionPhase; entrants: EntryOpenness } }) {
  const phasePill = status.phase === 'in_progress' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
      In progress
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
      Upcoming
    </span>
  );

  const entrantsPill = status.entrants === 'open' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
      Open for entries
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      Closed to new entrants
    </span>
  );

  return (
    <>
      {phasePill}
      {entrantsPill}
    </>
  );
}

export function PublicCompetitionCard({
  competition,
  onClick,
  onMouseEnter,
  totalEntrants = 0,
  remainingEntrants = null,
  wcFirstKickoff,
  firstGameweekDeadline,
}: PublicCompetitionCardProps) {
  const competitionType = competition.competitionType ?? competition.competition_type;
  const isLMS = competitionType === 'lms';
  const isWorldCup = competitionType === 'worldcup';
  const pct = totalEntrants > 0 && remainingEntrants !== null ? Math.round((remainingEntrants / totalEntrants) * 100) : 0;
  const status = getPublicCompetitionStatus(competition, { wcFirstKickoff, firstGameweekDeadline });
  const typeLabel = publicCompetitionTypeLabel(competitionType);

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`w-full bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 border-2 hover:shadow-lg transition-all group text-left ${
        isWorldCup
          ? 'border-amber-500/50 dark:border-amber-500/30 hover:border-amber-500'
          : isLMS
            ? 'border-green-500/50 dark:border-green-500/30 hover:border-green-500'
            : 'border-blue-500/50 dark:border-blue-500/30 hover:border-blue-500'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex-shrink-0 shadow-md ${
          isWorldCup
            ? 'bg-gradient-to-br from-amber-500 to-orange-600'
            : isLMS
              ? 'bg-gradient-to-br from-green-500 to-emerald-600'
              : 'bg-gradient-to-br from-blue-500 to-cyan-600'
        }`}>
          {isWorldCup ? (
            <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          ) : isLMS ? (
            <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          ) : (
            <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
              isWorldCup
                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                : isLMS
                  ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                  : 'bg-blue-500/20 text-blue-700 dark:text-blue-300'
            }`}>
              {typeLabel}
            </span>
          </div>
          <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white leading-tight">{competition.name}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <StatusBadges status={status} />
            {!isLMS && !isWorldCup && totalEntrants > 0 && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {totalEntrants} {totalEntrants === 1 ? 'entrant' : 'entrants'}
              </span>
            )}
            {isWorldCup && totalEntrants > 0 && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {totalEntrants} {totalEntrants === 1 ? 'entry' : 'entries'}
              </span>
            )}
          </div>
          {isLMS && totalEntrants > 0 && remainingEntrants !== null && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span>{remainingEntrants} of {totalEntrants} still standing</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`w-full py-2.5 rounded-xl text-sm font-bold text-white group-hover:opacity-90 transition-all shadow-md text-center ${
        isWorldCup
          ? 'bg-gradient-to-r from-amber-500 to-orange-600'
          : isLMS
            ? 'bg-gradient-to-r from-green-500 to-emerald-600'
            : 'bg-gradient-to-r from-blue-500 to-cyan-600'
      }`}>
        {status.entrants === 'open' ? 'Enter Competition' : 'View Competition'}
      </div>
    </button>
  );
}
